import { useMemo, useState } from "react";
import { Badge } from "@elaniintc/goes-design-system";
import {
  DropdownList,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";
import type { EntityType } from "@/schemas/digital-files";

export interface EntitySelectorProps {
  entities: EntityType[];
  selected?: EntityType;
  onSelect: (entity: EntityType) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

export const EntitySelector = ({
  entities,
  selected,
  onSelect,
  disabled,
  isLoading,
}: EntitySelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  // Sort entities alphabetically
  const sortedEntities = useMemo(
    () =>
      [...entities].sort((a, b) =>
        a.name.localeCompare(b.name, "es", { sensitivity: "base" }),
      ),
    [entities],
  );

  // Convert entities to dropdown items with search filtering
  const entityItems = useMemo(() => {
    const filtered = sortedEntities.filter((entity) =>
      entity.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    return filtered.map((entity) => ({
      label: (
        <span className="flex items-center gap-2 w-full">
          <span className="truncate">{entity.name}</span>
          {entity.version != null && (
            <Badge variant="neutral" size="sm" shape="pill">
              v{entity.version}
            </Badge>
          )}
        </span>
      ),
      value: entity.id,
      icon: { type: "far" as const, name: "database" as const },
    }));
  }, [sortedEntities, searchQuery]);

  const selectedItem = useMemo(() => {
    if (!selected) return undefined;
    return entityItems.find((item) => item.value === selected.id);
  }, [selected, entityItems]);

  return (
    <DropdownList
      options={[
        {
          groupId: "entities",
          groupLabels: ["Entidades"],
          items: entityItems,
        },
      ]}
      selectedValue={selectedItem}
      toggleSelection={(item: IDropdownListItem) => {
        const entity = entities.find((e) => e.id === item.value);
        if (entity) onSelect(entity);
      }}
      placeholder="Selecciona una entidad"
      search={{
        placeholder: "Buscar entidad...",
        query: searchQuery,
        onSearch: setSearchQuery,
      }}
      disabled={disabled || isLoading}
      widthClassName="w-full"
    />
  );
};
